import Vue from 'vue';
import Router from 'vue-router';
import AppLayout from '@/layouts/app-layout/app-layout.vue';
import AppHeaderLayout from '@/layouts/app-header-layout/app-header-layout.vue';
import { sessionService, SessionService } from '@/providers/services/auth/session';
import access from '@/permission/permission';
import 'pace-progressbar';
import * as LoginPage from 'navigator-widget-ui-component';
import * as Recruiters from 'job-board-admin-widget-ui';
import { component } from 'vue/types/umd';

Vue.use(Router);

const routes = [
  {
    path: '/',
    redirect: 'setup',
    component: AppHeaderLayout,
    children: [
      {
        path: '/setup',
        redirect: '/setup/view',
        component: () => import('@/routers/views/setup/setup.vue'),
        meta: { isRequiredAuth: true },
        children: [
          {
            path: 'user-list',
            component: () => import('@/routers/views/setup/role-permission/role-permission.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'subjects-facets',
            component: () => import('@/routers/views/setup/subjects-facets/subjects-facets.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'lor-facets',
            component: () => import('@/routers/views/setup/lor-facets/lor-facets.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'class-configuration',
            component: () => import('@/routers/views/setup/auto-class-config/auto-class-config.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'sso-setup',
            component: () => import('@/routers/views/setup/sso-setup/sso-setup.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'view',
            component: () => import('@/routers/views/setup/dashboard/dashboard.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'white-labeling',
            component: () => import('@/routers/views/setup/settings/white-labeling/white-labeling.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'class-settings',
            component: () => import('@/routers/views/setup/settings/class-settings/class-settings.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'translations-list',
            component: () => import('@/routers/views/setup/translations/translations.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'tenant-settings',
            name: 'tenant-settings-edit',
            component: () => import('@/routers/views/setup/tenant-settings-loader/tenant-settings-loader.vue'),
            meta: { isRequiredAuth: true },
            props: (route: any) => (route.query),
          },
          {
            path: 'settings',
            redirect: 'settings/localization',
            component: () => import('@/routers/views/setup/settings/settings.vue'),
            meta: { isRequiredAuth: true },
            children: [
              {
                path: 'localization',
                component: () => import('@/routers/views/setup/settings/localization/localization.vue'),
                meta: { isRequiredAuth: true },
              },
            ],
          },
        ],
      },
      {
        path: '/tenant',
        component: () => import('@/routers/views/tenant/tenant.vue'),
        meta: { isRequiredAuth: true },
        name: 'tenant',
      },
      {
        path: '/my-gooru',
        redirect: '/my-gooru/access-code',
        component: () => import('@/routers/views/mygooru/my-gooru.vue'),
        name: 'Mygooru',
        meta: { isRequiredAuth: true },
        children: [
          {
            path: 'access-code',
            name: 'access-code-page',
            component: () => import('@/components/my-gooru-pages/access-code/access-code.vue'),
          },
          {
            path: 'nudge-config',
            name: 'nudge-config-page',
            component: () => import('@/components/my-gooru-pages/nudge-config/nudge-config.vue'),
          },
          {
            path: 'nudge-types',
            name: 'nudge-types-page',
            component: () => import('@/components/my-gooru-pages/nudge-types/nudge-types.vue'),
          },

        ],
      },
      {
        path: '/community-hub',
        name: 'community-hub',
        component: () => import('@/routers/views/community-hub/community-hub.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/users-rosters',
        redirect: 'users-rosters/roster-syncs',
        component: () => import('@/routers/views/users/users.vue'),
        meta: { isRequiredAuth: true },
        name: 'users-rosters',
        children: [
          {
            path: 'roster-syncs',
            name: 'roster-syncs-page',
            component: () => import('@/components/rosters-pages/roster-sync-page/roster-sync-page.vue'),
          },
          {
            path: 'orgs',
            name: 'orgs-page',
            component: () => import('@/components/rosters-pages/organisations/organisations.vue'),
          },
          {
            path: 'schools',
            name: 'schools-page',
            component: () => import('@/components/rosters-pages/schools/schools.vue'),
          },
          {
            path: 'users',
            name: 'users-page',
            component: () => import('@/components/rosters-pages/users/users.vue'),
          },
          {
            path: 'classes',
            name: 'classes-page',
            component: () => import('@/components/rosters-pages/classes/classes.vue'),
          },
          {
            path: 'assign-school',
            name: 'assign-school-page',
            component: () => import('@/components/rosters-pages/assign-school/assign-school.vue'),
          },
          {
            path: 'roster-uploads',
            name: 'roster-uploads-page',
            component: () => import('@/routers/views/setup/roster-setup/tenant-data/roster-upload/roster-upload.vue'),
          },
          {
            path: 'roster-uploads-list',
            name: 'roster-uploads-list-page',
            component: () => import('@/components/rosters-pages/rosters-upload-list/rosters-upload-list.vue'),
          },
        ],
      },
      {
        path: 'roster/tenant/details',
        component: () => import('@/routers/views/setup/roster-setup/tenant-data/tenant-data.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/content-registry/:id',
        name: 'content-network-profile',
        component: () => import('@/routers/views/network/partners/partner-profile-details/partner-profile-details.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/tool-registry/:id',
        name: 'tool-network-profile',
        component: () => import('@/routers/views/network/partners/partner-profile-details/partner-profile-details.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/partner-registry/:id',
        name: 'partners-network-profile',
        component: () => import('@/routers/views/network/partners/partner-profile-details/partner-profile-details.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/content-registry',
        name: 'content-registry',
        component: () => import('@/routers/views/learner-registry/content-registry/content-registry.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/tool-registry',
        name: 'tool-registry',
        component: () => import('@/routers/views/learner-registry/tool-registry/tool-registry.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/partner-registry',
        name: 'partner-registry',
        component: () => import('@/routers/views/learner-registry/partner-registry/partner-registry.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/approval-request',
        name: 'approval-request',
        component: () => import('@/routers/views/approval-request/registration-approval/registration-approval.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/independent-study',
        name: 'independent',
        component: () => import('@/routers/views/independent-study/independent-study.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/partner-catalog',
        name: 'partner-catalog',
        redirect: '/partner-catalog/tool-registry',
        component: () => import('@/routers/views/partner-catalog/partner-catalog.vue'),
        meta: { isRequiredAuth: true },
        children: [
          {
            path: 'tool-registry',
            name: 'tool-registry-layout',
            redirect: '/tool-registry/view',
            component: () => import('@/routers/views/partner-catalog/tool-registry/tool-layout/tool-layout.vue'),
            children: [
              {
                path: '',
                name: 'tool-registry',
                component: () => import('@/routers/views/partner-catalog/tool-registry/tool-registry.vue'),
                meta: { isRequiredAuth: true },
              },
              {
                path: 'add-tool',
                name: 'tool-registry-form',
                component: () => import('@/routers/views/partner-catalog/tool-registry/create-tool/create-tool.vue'),
                meta: { isRequiredAuth: true },
              },
              {
                path: 'edit-tool',
                name: 'tool-registry-edit',
                component: () => import('@/routers/views/partner-catalog/tool-registry/edit-tool/edit-tool.vue'),
                meta: { isRequiredAuth: true },
              },
            ],
          },
          {
            path: 'content-registry',
            name: 'content-registry-layout',
            redirect: '/content-registry',
            component: () => import('./views/partner-catalog/content-registry/content-layout/content-layout.vue'),
            meta: { isRequiredAuth: true },
            children: [
              {
                path: '',
                name: 'content-registry',
                component: () => import('@/routers/views/partner-catalog/content-registry/content-registry.vue'),
                meta: { isRequiredAuth: true },
              },
              {
                path: 'add-product',
                name: 'add-product-detail',
                component: () => import('./views/partner-catalog/content-registry/create-product/create-product.vue'),
                meta: { isRequiredAuth: true },
              },
              {
                path: 'edit-product',
                name: 'content-registry-edit',
                component: () => import('@/routers/views/partner-catalog/content-registry/edit-product-details/edit-product-details.vue'),
                meta: { isRequiredAuth: true },
              },
            ],
          },
          {
            path: 'partners-list',
            name: 'providers-list',
            component: () => import('@/routers/views/partner-catalog/providers/providers.vue'),
            meta: { isRequiredAuth: true },
            props: (route: any) => (route.query),
          },
          {
            path: 'add',
            name: 'parnters-add',
            component: () => import('@/routers/views/partner-catalog/providers/partners/partners-add/partners-add'),
            meta: { isRequiredAuth: true },
          },
        ],
      },
      {
        path: '/console/partners',
        name: 'parnters-details',
        redirect: '/console/partners/collaborators-list',
        component: () => import('@/routers/views/console/partners/partners.vue'),
        meta: { isRequiredAuth: true },
        children: [
          {
            path: 'list/type/:type',
            name: 'parnters-details-list',
            component: () => import('@/routers/views/console/partners/partner-list/partner-list.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'collaborators-list',
            name: 'collaborators-details-list',
            component: () => import('@/routers/views/console/partners/collaborators-list/collaborators-list.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: ':id/details',
            name: 'parnters-details-edit',
            component: () => import('@/routers/views/console/partners/partners-edit/partners-edit.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'create',
            name: 'parnters-details-create',
            component: () => import('@/routers/views/console/partners/partners-edit/partners-edit.vue'),
            meta: { isRequiredAuth: true },
          },
        ],
      },
      {
        path: '/console',
        name: 'console',
        component: () => import('@/routers/views/console/console.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/console/:name',
        name: 'console-section',
        component: () => import('@/routers/views/console/lrs/lrs.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/console/impersonate/users',
        name: 'console-impersonate-users',
        component: () => import('@/routers/views/console/impersonate-list/impersonate-list.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/console/impersonate/navigator',
        name: 'console-impersonate-navigator',
        component: () => import('@/routers/views/console/impersonate-navigator/impersonate-navigator.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/users',
        name: 'users',
        component: () => import('@/routers/views/users/users.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/data-lake',
        name: 'data-lake',
        redirect: 'data-lake/view',
        component: () => import('@/routers/views/data-lake/data-lake.vue'),
        meta: { isRequiredAuth: true },
        children: [
          {
            path: 'view',
            component: () => import('@/routers/views/data-lake/dashboard/dashboard.vue'),
            meta: { isRequiredAuth: true },
          },
          {
            path: 'data-access',
            component: () => import('@/routers/views/data-lake/data-access/data-access.vue'),
            meta: { isRequiredAuth: true },
          },
        ],
      },
      {
        path: '/setting-keys',
        name: 'setting-keys',
        component: () => import('@/routers/views/setup/tenant-keys/tenant-keys.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/tenant-configurations',
        name: 'tenant-configurations',
        component: () => import('@/routers/views/tenant-configurations/tenant-configurations.vue'),
        meta: { isRequiredAuth: true },
      },
      {
        path: '/job-boards',
        name: 'recruiters',
        redirect: '/job-boards/dashboard',
        component: Recruiters.routerList()[0].component,
        children: Recruiters.routerList()[0].children,
      },
      {
        path: '/profile',
        name: 'profile',
        component: () => import('@/routers/views/profile/profile.vue'),
        meta: { isRequiredAuth: true },
      },
    ],

  },
  {
    path: '/',
    redirect: 'login',
    component: AppLayout,
    children: [
      ...LoginPage.routerList(),
      {
        path: '/logout',
        name: 'logout',
        beforeEnter() {
          sessionService.deleteSession();
          window.location.href = '/login';
        },
      },
    ],
  },


   // Mobile view//
   {
    path: '/mv-users-rosters',
    component: () => import('@/routers/m-views/m-users/m-users.vue'),
    meta: { isRequiredAuth: true },
    name: 'm-users-rosters',
    children: [],
  },
];

const router = new Router({
  linkExactActiveClass: 'exact-active',
  linkActiveClass: 'active',
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve({ x: 0, y: 0 });
      }, 500);
    });
  },
});

router.beforeEach(async (to, from, next) => {
  // Set the document title dynamically
  const isMyGooruHost = window.location.host.includes('mygooru.ai');
  document.title = `${isMyGooruHost ? 'MyGooru Admin' : 'Admin'}`;
  if (to && to.meta && to.meta.isRequiredAuth && !sessionService.isAuthorized()) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  } else if (to && to.meta && to.meta.isRequiredAuth && !access.isTenantConfigured) {
    if (to.path !== '/tenant-configurations') {
      return next({ path: `/tenant-configurations` });
    }
  } else if (to && to.meta && to.meta.permission && !access.hasMenuAccess(to.meta.permission)) {
    return next({ path: `/${access.landingPage()}` });
  } else if (to && to.meta && to.meta.isVerifyLogin && sessionService.getMcUpdate()) {
    // Note: if ember js removed no need to use this
    if (sessionService.getMcUpdate().code !== 'DEMO_USER') {
      access.doLoginInWithCredential(sessionService.getMcUpdate());
      return;
    }
    const session = sessionService.getDemoSessionCopy();
    if (session) {
      access.updateRole(session);
    }
  } else if (to.query.isImpersonate) {
    const session = sessionService.getDemoSessionCopy();
    if (session) {
      access.updateRole(session, false).then((landingPage) => {
        next({ path: `${landingPage}` });
      });
    }
  }
  next();
});

export default router;
